import React, { useState, useEffect, useCallback } from "react"
import { useLocation } from "react-router-dom"
import { useAuth0 } from "@auth0/auth0-react"
import { useQueryClient } from "@tanstack/react-query"
import _ from "lodash"

import ChairTable from "../components/Settings/ChairTable"
import AcuityTable from "../components/Settings/AcuityTable"
import EligibleNursesTable from "../components/Settings/EligibleNursesTable"
import PersonalSettings from "../components/Settings/PersonalSettings"
import NotificationPopup from "../components/DailyCalendarFiles/CustomCalendar/NotificationPopup" // Adjust the import path
import { PageLoader } from "../components/Authentication/page-loader" // Adjust the import path as necessary
import useFetchSettings from "../hooks/useFetchSettings"
import { usePermissions } from "../hooks/queries/usePermissions"
import Goals from "../components/Settings/Goals"
import CenterOpening from "../components/Settings/CenterOpening"
import NurseBreak from "../components/Settings/NurseBreak"
import NurseAssignment from "../components/Settings/NurseAssignment"
import { useAcuityTable } from "../hooks/useAcuityTable"
import useLogging from "../hooks/useLogging"
function classNames(...classes) {
	return classes.filter(Boolean).join(" ")
}

function Settings({ sidebarOpen }) {
	const { isLoading, getAccessTokenSilently } = useAuth0()
	const queryClient = useQueryClient()
	const location = useLocation()
	const logging = useLogging()
	const [errors, setErrors] = useState({}) // State for form errors
	const [formData, setFormData] = useState({
		// State for form data
		centerOpen: [],
		openTime: [],
		closeTime: [],
		break_duration: "",
		break_start_time: "",
		break_end_time: "",
		patientNurseRatio: "",
		maxAcuityForCenter: "",
		maxAcuityForNurse: "",
		maxAcuityForCenterAtAnyTime: "",
		maxAcuityForNurseAtAnyTime: "",
		toggleRules: {},
		goals: [],
		nurses: []
	})

	const [initialFormData, setInitialFormData] = useState(formData) // Store initial form data

	// Function to check if formData has changed
	const hasFormDataChanged = useCallback(() => {
		return JSON.stringify(formData) !== JSON.stringify(initialFormData)
	}, [formData, initialFormData])

	const { data: permissionsPayload } = usePermissions()
	const permissions = permissionsPayload?.permissions ?? null

	const { settingsData, settingsStatus, settingsError } = useFetchSettings()

	const [chairs, setChairs] = useState([])
	const [notification, setNotification] = useState({ visible: false, message: "", success: true })
	const [hasWriteSettingsPermission, setHasWriteSettingsPermission] = useState(false)

	const [tabs, setTabs] = useState([
		{ name: "Infusion Center Settings", href: "#infusion", current: true },
		{ name: "Personal Settings", href: "#personal", current: false },
		{ name: "Optimizer Goals", href: "#goals", current: false }
	])

	// Update tabs based on the current URL hash
	useEffect(() => {
		const currentHash = location.hash || "#infusion" // Default to #infusion if no hash is present

		setTabs(prevTabs =>
			prevTabs.map(tab => ({
				...tab,
				current: tab.href === currentHash
			}))
		)
	}, [location.hash])

	useEffect(() => {
		if (settingsData) {
			setFormData(settingsData)
			setInitialFormData(settingsData)
		}
	}, [settingsData])

	const fetchChairs = useCallback(async () => {
		try {
			const token = await getAccessTokenSilently({
				audience: process.env.REACT_APP_API_IDENTIFIER
			})

			const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/chairs`, {
				method: "GET",
				headers: {
					Authorization: `Bearer ${token}`
				}
			})

			if (!response.ok) {
				throw new Error("Failed to fetch chairs")
			}

			const data = await response.json()
			setChairs(data.payload?.chairs || [])
		} catch (error) {
			logging.error("Error fetching chairs:", error)
		}
	}, [getAccessTokenSilently, logging])

	useEffect(() => {
		if (permissions && permissions.includes("read:settings")) {
			fetchChairs()
		}
	}, [fetchChairs, permissions])

	useEffect(() => {
		// Set hasWriteSettingsPermission based on initial permissions
		if (permissions && permissions.includes("write:settings")) {
			setHasWriteSettingsPermission(true)
		}
	}, [permissions])

	const checkTimeFormat = useCallback(time => {
		return !time || !/^\d{2}:\d{2}$/.test(time)
	}, [])

	const checkNumber = useCallback(number => {
		return !number || isNaN(parseInt(number, 10)) || !/^\d+$/.test(number)
	}, [])

	const isStartTimeBeforeEndTime = useCallback((startTime, endTime) => {
		if (!startTime || !endTime) return true

		const [startHours, startMinutes] = startTime.split(":").map(Number)
		const [endHours, endMinutes] = endTime.split(":").map(Number)

		const startDate = new Date(1970, 0, 1, startHours, startMinutes)
		const endDate = new Date(1970, 0, 1, endHours, endMinutes)

		return startDate < endDate
	}, [])

	const validateSettings = useCallback(
		data => {
			const errors = {}

			const timeFields = ["openTime", "closeTime", "break_start_time", "break_end_time"]
			const integerFields = [
				"break_duration",
				"patientNurseRatio",
				"maxAcuityForCenterAtAnyTime",
				"maxAcuityForNurseAtAnyTime",
				"maxAcuityForCenter",
				"maxAcuityForNurse"
			]

			// Time field validation
			timeFields.forEach(field => {
				// Check if the field is an array and handle each element
				if (typeof data[field] === "object") {
					for (const [index, value] of Object.entries(data[field])) {
						if (checkTimeFormat(value)) {
							errors[`${field}.${index}`] =
								`Must be in the format "HH:MM" and cannot be empty.`
						}
					}
				} else {
					// If not an array, handle as a single value
					const value = data[field]
					if (checkTimeFormat(value)) {
						errors[field] = `Must be in the format "HH:MM" and cannot be empty.`
					}
				}
			})

			// Validate if each openTime is before its corresponding closeTime
			if (!errors["openTime"] && !errors["closeTime"]) {
				if (typeof data.openTime === "object" && typeof data.closeTime === "object") {
					// Loop over the elements in openTime and closeTime if they are objects (e.g., arrays or objects with indexed keys)
					for (const [index, openTime] of Object.entries(data.openTime)) {
						const closeTime = data.closeTime[index]

						if (!isStartTimeBeforeEndTime(openTime, closeTime)) {
							errors[`openTime.${index}`] = "Open Time must be before Close Time."
							errors[`closeTime.${index}`] = "Close Time must be after Open Time."
						}
					}
				} else {
					// If openTime and closeTime are single string values, validate them directly
					const openTime = data.openTime
					const closeTime = data.closeTime

					if (!isStartTimeBeforeEndTime(openTime, closeTime)) {
						errors["openTime"] = "Open Time must be before Close Time."
						errors["closeTime"] = "Close Time must be after Open Time."
					}
				}
			}

			// Validation for break_start_time and break_end_time
			if (!errors.break_start_time && !errors.break_end_time) {
				if (!isStartTimeBeforeEndTime(data.break_start_time, data.break_end_time)) {
					errors.break_start_time = "Start Time must be before End Time."
					errors.break_end_time = "End Time must be after Start Time."
				}
			}

			// Integer field validation
			integerFields.forEach(field => {
				const value = data[field]
				if (checkNumber(value)) {
					errors[field] = `Must be an integer and cannot be empty.`
				}
			})

			// Validate goals
			if (typeof data.goals === "object") {
				for (const [index, goal] of Object.entries(data.goals)) {
					if (!goal.name || goal.name.trim() === "") {
						errors[`goals.${index}.name`] = "Goal name cannot be empty."
					}
					if (!goal.value || goal.value.trim() === "") {
						errors[`goals.${index}.value`] = "Goal value cannot be empty."
					}
				}
			} else {
				errors.goals = "Goals must be a valid array or object with entries."
			}

			return errors
		},
		[checkTimeFormat, isStartTimeBeforeEndTime, checkNumber]
	)

	const [acuityTableModified, setAcuityTableModified] = useState(false)
	const { acuityTable, refetch: refetchAcuityTable } = useAcuityTable()
	const [localAcuityTable, setLocalAcuityTable] = useState([])

	useEffect(() => {
		if (!acuityTable) return
		setLocalAcuityTable(acuityTable)
	}, [acuityTable])

	const [chairsModified, setChairsModified] = useState(false)

	const handleSave = useCallback(async () => {
		logging.info("Settings - Saving settings", {
			component: "Settings",
			function: "handleSave"
		})
		const validationErrors = validateSettings(formData)
		if (Object.keys(validationErrors).length > 0) {
			logging.info("Settings - Validation errors found:", {
				component: "Settings",
				function: "handleSave",
				validationErrors: validationErrors
			})
			setErrors(validationErrors) // Set errors state
			return
		}

		try {
			// Retrieve the access token for authentication
			const token = await getAccessTokenSilently({
				audience: process.env.REACT_APP_API_IDENTIFIER
			})

			// Build the full normalized payload (with defaults/coercion)
			const fullPayload = {
				centerOpen: formData.centerOpen || [
					false,
					false,
					false,
					false,
					false,
					false,
					false
				],
				openTime: formData.openTime || ["", "", "", "", "", "", ""],
				closeTime: formData.closeTime || ["", "", "", "", "", "", ""],
				break_duration: Number(formData.break_duration) || 0,
				break_start_time: formData.break_start_time || "",
				break_end_time: formData.break_end_time || "",
				patientNurseRatio: Number(formData.patientNurseRatio) || 0,
				maxAcuityForCenterAtAnyTime: Number(formData.maxAcuityForCenterAtAnyTime) || 0,
				maxAcuityForNurseAtAnyTime: Number(formData.maxAcuityForNurseAtAnyTime) || 0,
				maxAcuityForCenter: Number(formData.maxAcuityForCenter) || 0,
				maxAcuityForNurse: Number(formData.maxAcuityForNurse) || 0,
				toggleRules: formData.toggleRules || {},
				nurses: formData.nurses || [],
				liveDate: formData.liveDate || null
			}

			// Diff form fields against initialFormData; only include changed keys.
			// Backend updateSettings does a $set so omitted fields stay untouched.
			const patch = {}
			for (const key of Object.keys(fullPayload)) {
				if (!_.isEqual(fullPayload[key], initialFormData?.[key])) {
					patch[key] = fullPayload[key]
				}
			}

			// chairs and acuityTable live outside formData and have explicit modified
			// flags — include only when they've actually changed.
			if (chairsModified) {
				patch.chairs = chairs || []
				patch.numberOfChairs = (chairs || []).length
			}
			if (acuityTableModified) {
				patch.acuityTable = localAcuityTable
			}

			if (Object.keys(patch).length === 0) {
				setNotification({
					visible: true,
					message: "No changes to save",
					success: true
				})
				setTimeout(() => setNotification({ visible: false, message: "" }), 2000)
				return
			}

			logging.info("Settings - Sending patch", {
				component: "Settings",
				function: "handleSave",
				changedKeys: Object.keys(patch)
			})

			// Make the API call to update settings
			const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/settings`, {
				method: "POST",
				headers: {
					"Content-Type": "application/json",
					Authorization: `Bearer ${token}`
				},
				body: JSON.stringify(patch)
			})

			if (!response.ok) {
				throw new Error("Failed to update settings")
			}

			refetchAcuityTable()

			queryClient.invalidateQueries({ queryKey: ["settings"] })
			setChairsModified(false)
			setAcuityTableModified(false)
			setInitialFormData(formData)

			// Display success notification
			setNotification({
				visible: true,
				message: "Settings Successfully Updated",
				success: true
			})

			logging.info("Settings - Updated Settings Payload", {
				component: "Settings",
				function: "handleSave"
			})

			setTimeout(() => {
				setNotification({
					visible: false,
					message: ""
				})
			}, 3000)
		} catch (error) {
			logging.error("Error updating settings:", error)

			// Display error notification
			setNotification({
				visible: true,
				message: "Error updating settings",
				success: false
			})

			setTimeout(() => {
				setNotification({
					visible: false,
					message: ""
				})
			}, 3000)
		}
		// Helpers/setters are stable within this component context; deps intentionally curated.
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [
		queryClient,
		getAccessTokenSilently,
		notification,
		setNotification,
		validateSettings,
		localAcuityTable,
		refetchAcuityTable,
		formData,
		logging,
		chairs
	])

	if (permissions && permissions.includes("read:settings")) {
		if (
			isLoading ||
			!permissions ||
			!settingsData ||
			settingsData.length === 0 ||
			settingsStatus === "loading" ||
			settingsError ||
			!localAcuityTable ||
			!chairs ||
			!formData
		) {
			return <PageLoader /> // Show loading state while fetching
		}
	}

	return (
		<>
			<div className={`${sidebarOpen ? "pl-64" : "pl-8"}`}>
				{notification.visible && (
					<NotificationPopup
						message={notification.message}
						success={notification.success}
						onUndo={() =>
							setNotification({ visible: false, message: "", success: true })
						}
					/>
				)}

				<div className="px-8">
					<div className="flex flex-col lg:max-w-6xl mx-auto min-w-[600px]">
						<main className="flex-1">
							<div className="relative mx-auto max-w-6xl">
								<div className="pt-10">
									<div>
										<h1 className="text-3xl font-bold tracking-tight text-custom-blue">
											Settings
										</h1>
									</div>
									<div>
										<div className="py-6">
											<div className="lg:hidden">
												{settingsData && (
													<>
														<label
															htmlFor="selected-tab"
															className="sr-only"
														>
															Select a tab
														</label>
														<select
															id="selected-tab"
															name="selected-tab"
															className="mt-1 block w-full rounded-md border-0 py-1.5 pl-3 pr-10 text-gray-900 ring-1 ring-inset ring-gray-300 focus:ring-2 focus:ring-inset focus:ring-purple-500 sm:text-sm sm:leading-6"
															value={
																tabs.find(tab => tab.current)?.name
															}
															onChange={e => {
																const selectedTabName =
																	e.target.value
																setTabs(prevTabs =>
																	prevTabs.map(t => ({
																		...t,
																		current:
																			t.name ===
																			selectedTabName
																	}))
																)
															}}
														>
															{tabs.map(tab => (
																<option
																	key={tab.name}
																	value={tab.name}
																>
																	{tab.name}
																</option>
															))}
														</select>
													</>
												)}
											</div>
											<div className="hidden lg:block">
												<div className="border-b border-gray-200">
													{settingsData && (
														<>
															<nav className="-mb-px flex space-x-8">
																{tabs.map(tab => (
																	<a
																		key={tab.name}
																		href={tab.href}
																		onClick={e => {
																			e.preventDefault()
																			setTabs(prevTabs =>
																				prevTabs.map(t => ({
																					...t,
																					current:
																						t.name ===
																						tab.name
																				}))
																			)
																		}}
																		className={classNames(
																			tab.current
																				? "border-customBlue text-customBlue"
																				: "border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700",
																			"whitespace-nowrap border-b-2 px-1 py-4 text-sm font-medium"
																		)}
																	>
																		{tab.name}
																	</a>
																))}
															</nav>
														</>
													)}
												</div>
											</div>
										</div>
									</div>
								</div>

								<div className="grid grid-cols-12 gap-4">
									<div className="col-span-12 lg:col-span-12 mx-auto">
										{settingsData && tabs[0].current && (
											<div className="">
												<div className="mt-6">
													<form
														onSubmit={e => {
															e.preventDefault()
															handleSave()
														}}
													>
														<div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
															<div className="col-span-2 sm:col-span-2 min-w-[1100px]">
																<CenterOpening
																	formData={formData}
																	setFormData={setFormData}
																	errors={errors}
																	hasWriteSettingsPermission={
																		hasWriteSettingsPermission
																	}
																/>
															</div>

															<div className="col-span-2 min-w-[1100px]">
																<NurseBreak
																	formData={formData}
																	setFormData={setFormData}
																	errors={errors}
																	hasWriteSettingsPermission={
																		hasWriteSettingsPermission
																	}
																/>
															</div>

															<div className="col-span-2 min-w-[1100px]">
																<NurseAssignment
																	formData={formData}
																	setFormData={setFormData}
																	errors={errors}
																	hasWriteSettingsPermission={
																		hasWriteSettingsPermission
																	}
																/>
															</div>
														</div>

														<ChairTable
															hasWriteSettingsPermission={
																hasWriteSettingsPermission
															}
															chairs={chairs}
															setChairs={setChairs}
															setChairsModified={setChairsModified}
														/>
														<AcuityTable
															hasWriteSettingsPermission={
																hasWriteSettingsPermission
															}
															acuityTable={localAcuityTable}
															setAcuityTable={setLocalAcuityTable}
															nurses={settingsData.nurses} // TODO: remind me to change this
															setAcuityTableModified={
																setAcuityTableModified
															}
														/>
														<EligibleNursesTable
															hasWriteSettingsPermission={
																hasWriteSettingsPermission
															}
															formData={formData}
															setFormData={setFormData}
															errors={errors}
														/>

														<button
															type="submit"
															disabled={
																!hasFormDataChanged() &&
																!acuityTableModified &&
																!chairsModified
															}
															className={`${
																!hasFormDataChanged() &&
																!acuityTableModified &&
																!chairsModified
																	? "bg-gray-400 cursor-not-allowed"
																	: "bg-customBlue hover:bg-custom-blue cursor-pointer"
															} rounded-md px-3 py-2 text-sm font-semibold text-white shadow-sm mx-auto my-10 block`}
														>
															Update
														</button>
													</form>
												</div>
											</div>
										)}

										{tabs[1].current && (
											<PersonalSettings
												handleSubmit={handleSave}
												nurses={settingsData?.nurses}
											/>
										)}
										{settingsData && tabs[2].current && (
											<Goals
												hasWriteSettingsPermission={
													hasWriteSettingsPermission
												}
												settingsData={settingsData}
												formData={formData}
												setFormData={setFormData}
												errors={errors}
												setErrors={setErrors}
												setNotification={setNotification}
											/>
										)}
									</div>
								</div>
							</div>
						</main>
					</div>
				</div>
			</div>
		</>
	)
}

export default Settings
